/**
 * In-app update check (native only).
 *
 * Compares the running Capacitor build number against the latest store build
 * published in `native_app_releases` (read through the `native-app-version`
 * edge function). Web builds always report "unknown" and never prompt.
 *
 *  - build < min_supported_build  -> "required"  (blocking prompt, no dismiss)
 *  - build < latest build         -> "optional"  (dismissable, snoozed 24h)
 *  - otherwise                    -> "current"
 */
import { supabase } from "@/integrations/supabase/client";
import { getPlatform, isNativeApp, type NativePlatform } from "./platform";

export type UpdateStatus = "unknown" | "current" | "optional" | "required";

export interface StoreRelease {
  platform: "ios" | "android";
  version: string;
  build: number;
  min_supported_build: number;
  store_url: string | null;
  release_notes: string | null;
  released_at: string | null;
}

export interface UpdateCheckResult {
  status: UpdateStatus;
  /** Installed app version, e.g. "1.0.0". */
  currentVersion: string | null;
  /** Installed build number (iOS CFBundleVersion / Android versionCode). */
  currentBuild: number | null;
  release: StoreRelease | null;
}

const SNOOZE_KEY = "hibane_update_snooze";
const SNOOZE_MS = 24 * 60 * 60 * 1000;

const noUpdate: UpdateCheckResult = {
  status: "unknown",
  currentVersion: null,
  currentBuild: null,
  release: null,
};

/** Installed native version/build, or nulls on web / when unavailable. */
export const getInstalledAppInfo = async (): Promise<{
  version: string | null;
  build: number | null;
}> => {
  if (!isNativeApp()) return { version: null, build: null };
  try {
    const { App } = await import("@capacitor/app");
    const info = await App.getInfo();
    const build = Number.parseInt(String(info.build ?? ""), 10);
    return {
      version: info.version ?? null,
      build: Number.isFinite(build) ? build : null,
    };
  } catch {
    return { version: null, build: null };
  }
};

const fetchLatestRelease = async (platform: NativePlatform): Promise<StoreRelease | null> => {
  if (platform === "web") return null;
  try {
    const { data, error } = await supabase.functions.invoke("native-app-version", {
      body: { platform },
    });
    if (error) return null;
    const payload = data as { ok?: boolean; release?: StoreRelease | null } | null;
    if (!payload?.ok || !payload.release) return null;
    return payload.release;
  } catch {
    return null;
  }
};

/** Run the version check. Fails soft: any problem returns status "unknown". */
export const checkForAppUpdate = async (): Promise<UpdateCheckResult> => {
  if (!isNativeApp()) return noUpdate;

  const platform = getPlatform();
  const [{ version, build }, release] = await Promise.all([
    getInstalledAppInfo(),
    fetchLatestRelease(platform),
  ]);

  if (!release || build === null) {
    return { ...noUpdate, currentVersion: version, currentBuild: build };
  }

  let status: UpdateStatus = "current";
  if (build < release.min_supported_build) status = "required";
  else if (build < release.build) status = "optional";

  return { status, currentVersion: version, currentBuild: build, release };
};

/** True when the user asked to be reminded later about this exact build. */
export const isUpdateSnoozed = (build: number): boolean => {
  try {
    const raw = localStorage.getItem(SNOOZE_KEY);
    if (!raw) return false;
    const parsed = JSON.parse(raw) as { build?: number; at?: number };
    if (parsed?.build !== build || typeof parsed.at !== "number") return false;
    return Date.now() - parsed.at < SNOOZE_MS;
  } catch {
    return false;
  }
};

export const snoozeUpdate = (build: number): void => {
  try {
    localStorage.setItem(SNOOZE_KEY, JSON.stringify({ build, at: Date.now() }));
  } catch {
    /* storage unavailable — prompt again next launch */
  }
};

/** Default store listing when a release row has no explicit store_url. */
export const fallbackStoreUrl = (platform: NativePlatform, appId: string): string =>
  platform === "ios"
    ? "https://apps.apple.com/app/hibane-desk"
    : `https://play.google.com/store/apps/details?id=${appId}`;

/** Open the store listing in the system browser / store app. */
export const openStoreListing = async (release: StoreRelease | null): Promise<void> => {
  const url =
    release?.store_url ??
    fallbackStoreUrl(getPlatform(), "app.hibanedesk.mobile");
  try {
    window.open(url, "_system", "location=yes");
  } catch {
    window.location.href = url;
  }
};
